// Eskom PARCC — BULK TRANSACTION UPDATE from Excel (Lindiwe R6, 23 Jul 2026).
//
// "We would like to be able to update transactions via Excel bulk upload. The
// same mechanism as a reviewer manually updating a transaction, except done via
// bulk Excel."
//
// This is NOT the Import screen. Import owns loading, replacing and reviving a
// population; it may create rows and its update path is deliberately narrowed to
// source columns (WS2, the 1 Jul 2026 incident). This screen owns targeted edits
// to transactions that ALREADY EXIST, across the full surface a person can reach
// through the manual editors — and it never inserts. A reference the register
// does not carry is an operator mistake and is reported.
//
//   1. PREVIEW — the workbook is parsed locally (no writes). Rows are matched to
//      the register, then DIFFED CELL BY CELL. The operator sees every
//      `field: old → new` before anything happens, with review/work-product
//      changes flagged, and a row that changes nothing is marked Unchanged and
//      skipped.
//   2. APPLY — each included row runs sequentially: rowVersion guard →
//      bulkUpdateTransaction (field-locked write + per-row audit naming the
//      changed fields) → optional findings MERGE → gaps-status rollup. A failing
//      row is recorded and the batch CONTINUES.
//
// The five rules that make a manual-editor-width blast radius safe are documented
// on BULK_UPDATE_COLUMNS; four of them live here (present-columns-only is in the
// parser). Permission: admin-only screen, plus canEditTransaction per row.
import { useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  useToast,
  type Column,
} from '../../components/ui';
import { useTransactions, useFindings, useReplaceFindings } from '../../hooks';
import { useAuth } from '../../lib/auth';
import { checkRowVersion } from '../../lib/concurrency';
import { bulkUpdateTransaction, recordAudit, withAuditSuppressed } from '../../lib/dbWrite';
import { canBulkUpdateTransactions } from '../../lib/permissions';
import { queryClient } from '../../lib/queryClient';
import { invalidateRegister } from '../../lib/queryKeys';
import { rollupGapStatusFromFindings } from '../../domain/gapClosure';
import { mergeImportFindings, refKey } from '../import/importParse';
import type { FindingRead } from '../../lib/db';
import { parseBulkUpdateWorkbook, type BulkUpdateParse } from './bulkUpdateParse';
import { matchBulkUpdate, COLUMN_LABELS, type BulkUpdateDraft } from './matchBulkUpdate';
import { ImportBulkTabs } from '../import/ImportBulkTabs';

const ACCEPT = '.xlsx,.xlsm,.xls,.csv';

/** How many field changes render inline per row before the "+N more" expander. */
const INLINE_CHANGES = 4;

const OUTCOME_LABELS: Record<BulkUpdateDraft['outcome'], string> = {
  ready: 'Will update',
  unchanged: 'Unchanged — nothing to write',
  'not-found': 'Not in the register',
  ambiguous: 'Ambiguous match',
  'not-editable': 'Not editable by you',
  'no-reference': 'No reference number',
  duplicate: 'Duplicate (a later row wins)',
};

interface ResultRow {
  rowNo: number;
  reference: string;
  outcome: 'Updated' | 'Error';
  fields: number;
  message: string;
}

/** The one summary sentence, used verbatim by the toast, the audit and the panel. */
function summarise(fileName: string, updated: number, errors: number, fields: number): string {
  const parts = [`${updated} transaction(s) updated`, `${fields} field change(s)`];
  if (errors) parts.push(`${errors} error(s)`);
  return `Bulk update "${fileName}": ${parts.join(', ')}.`;
}

export function BulkUpdatePage(): React.JSX.Element {
  const { grant } = useAuth();
  const { show } = useToast();
  const transactionsQuery = useTransactions();
  const findingsQuery = useFindings();
  const replaceFindings = useReplaceFindings();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [parsed, setParsed] = useState<BulkUpdateParse | null>(null);
  const [drafts, setDrafts] = useState<BulkUpdateDraft[] | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const allowed = canBulkUpdateTransactions(grant);

  const writable = useMemo(() => (drafts ?? []).filter((d) => d.outcome === 'ready'), [drafts]);
  const changedFields = useMemo(
    () => writable.reduce((n, d) => n + d.changes.length + (d.findingsChanged ? 1 : 0), 0),
    [writable],
  );
  const touchesReview = useMemo(
    () => writable.some((d) => d.changes.some((c) => c.review) || d.findingsChanged),
    [writable],
  );

  const findingsByRef = useMemo(() => {
    const map = new Map<string, FindingRead[]>();
    for (const f of (findingsQuery.data ?? []) as FindingRead[]) {
      const key = refKey(f.referenceNo);
      if (!key) continue;
      const bucket = map.get(key);
      if (bucket) bucket.push(f);
      else map.set(key, [f]);
    }
    return map;
  }, [findingsQuery.data]);

  function reset(): void {
    setFileName('');
    setParseError('');
    setParsed(null);
    setDrafts(null);
    setResults(null);
    setProgress('');
    setExpanded(new Set());
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleFile(file: File): Promise<void> {
    reset();
    setFileName(file.name);
    // Matching needs the register. Distinguish "still loading" from "failed to
    // load" — the CES upload conflates them and a failed read reads as loading.
    if (transactionsQuery.error) {
      setParseError(
        'The transaction register could not be loaded, so no row can be matched. Retry in a moment.',
      );
      return;
    }
    if (transactionsQuery.isLoading || !transactionsQuery.data) {
      setParseError('The transaction register is still loading — try again in a moment.');
      return;
    }
    try {
      const isCsv = /\.csv$/i.test(file.name);
      const result = parseBulkUpdateWorkbook(isCsv ? await file.text() : await file.arrayBuffer());
      if (!result.rows.length) {
        throw new Error('No data rows were found in this file.');
      }
      if (!result.presentColumns.length) {
        throw new Error(
          'This file carries no updatable columns. Expected population headings such as Division, OU, Procurement Mechanism, Target Value or OpenText link.',
        );
      }
      setParsed(result);
      const matched = matchBulkUpdate({
        rows: result.rows,
        transactions: transactionsQuery.data,
        findingsByRef,
        grant,
      });
      setDrafts(matched.drafts);
    } catch (error) {
      setParsed(null);
      setDrafts(null);
      setParseError(error instanceof Error ? error.message : 'The file could not be read.');
      show('Could not read the file. See the message on the page.', 'error');
    }
  }

  function toggleRow(rowNo: number): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowNo)) next.delete(rowNo);
      else next.add(rowNo);
      return next;
    });
  }

  async function runUpdate(): Promise<void> {
    if (!allowed || !drafts || running) return;
    if (!writable.length) {
      show('No rows would change anything.', 'error');
      return;
    }
    setRunning(true);
    setResults(null);

    const out: ResultRow[] = [];
    let updated = 0;
    let fields = 0;

    // The per-row bulkUpdateTransaction audit already names the changed fields;
    // suppress the LOW-LEVEL write audits underneath (the findings replace) so a
    // 300-row upload leaves 300 readable lines, not 900.
    await withAuditSuppressed(async () => {
      for (const [index, draft] of writable.entries()) {
        const tx = draft.tx!;
        setProgress(`Updating ${index + 1} of ${writable.length} — ${tx.referenceNo}…`);
        try {
          // Guard against the PREVIEW-time rowVersion: anything that moved the
          // transaction since the diff was computed must surface as a conflict,
          // not be written over with a stale before-picture.
          const guard = await checkRowVersion(tx.referenceNo, tx.rowVersion);
          if (!guard.ok) {
            out.push({
              rowNo: draft.rowNo,
              reference: tx.referenceNo,
              outcome: 'Error',
              fields: 0,
              message:
                guard.current === null
                  ? 'The transaction no longer exists.'
                  : 'Changed by someone else since the preview — re-upload this row.',
            });
            continue;
          }

          // Findings MERGE, never replace: import-sourced lines are superseded,
          // analyst/CES-captured work is preserved. A spreadsheet column cannot
          // express "delete this specific captured line", so it must not be able
          // to. Then re-derive gapsStatus from the resulting set — it is a rollup
          // (gapClosure), so a value written straight from the sheet would be
          // overwritten by the next manual save anyway.
          const key = refKey(tx.referenceNo);
          const patch = { ...draft.patch };
          let merged: ReturnType<typeof mergeImportFindings> | null = null;
          if (draft.findingsChanged) {
            merged = mergeImportFindings(findingsByRef.get(key) ?? [], draft.findings);
            patch.gapsStatus = rollupGapStatusFromFindings(merged);
          }

          const changedLabels = draft.changes.map((c) => COLUMN_LABELS[c.column]);
          if (draft.findingsChanged) changedLabels.push('Findings');
          const note = `Bulk update from "${fileName}" (row ${draft.rowNo}) — ${changedLabels.join(', ')}`;

          // Carry the per-cell from/to into the audit payload, in the shared
          // FieldChange shape the timeline renders (so bulk reads like a Manage
          // edit), not just the field labels the note already holds.
          const changes = draft.changes.map((c) => ({
            field: c.column,
            label: COLUMN_LABELS[c.column],
            from: c.from,
            to: c.to,
          }));
          await bulkUpdateTransaction(patch, { note, changes });
          if (merged) {
            await replaceFindings.mutateAsync({ referenceNo: tx.referenceNo, findings: merged });
          }

          updated += 1;
          fields += changedLabels.length;
          out.push({
            rowNo: draft.rowNo,
            reference: tx.referenceNo,
            outcome: 'Updated',
            fields: changedLabels.length,
            message: changedLabels.join(', '),
          });
        } catch (error) {
          out.push({
            rowNo: draft.rowNo,
            reference: tx.referenceNo,
            outcome: 'Error',
            fields: 0,
            message: error instanceof Error ? error.message : 'The update failed.',
          });
        }
      }
    });

    const errors = out.length - updated;
    const summary = summarise(fileName, updated, errors, fields);
    try {
      await recordAudit({
        action: 'bulk_update',
        entityType: 'Transaction',
        entityId: fileName,
        note: summary,
      });
    } catch {
      // Best-effort — the per-transaction entries already carry the trail.
    }
    invalidateRegister(queryClient);
    setProgress('');
    setResults(out);
    setRunning(false);
    show(summary, errors ? 'error' : 'success');
  }

  if (!allowed) {
    return (
      <Card>
        <h3>Bulk update transactions</h3>
        <p className="small">
          Bulk updating the register is restricted to administrators. Ask an administrator if you
          need a population change applied.
        </p>
      </Card>
    );
  }

  if (transactionsQuery.isLoading) return <LoadingState message="Loading the register…" />;
  if (transactionsQuery.error) {
    const err = transactionsQuery.error;
    return (
      <ErrorState
        message="Could not load the register; a bulk update needs it to match every row."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => {
          void transactionsQuery.refetch();
        }}
      />
    );
  }

  const previewColumns: Column<BulkUpdateDraft>[] = [
    { key: 'rowNo', header: '#', align: 'right', sortable: true, render: (d) => String(d.rowNo) },
    {
      key: 'reference',
      header: 'Reference',
      sortable: true,
      render: (d) => d.referenceNo || <span className="small">(blank)</span>,
    },
    { key: 'division', header: 'Division', render: (d) => d.tx?.division ?? '' },
    {
      key: 'changes',
      header: 'Changes',
      align: 'right',
      sortable: true,
      accessor: (d) => d.changes.length + (d.findingsChanged ? 1 : 0),
      render: (d) => {
        const n = d.changes.length + (d.findingsChanged ? 1 : 0);
        return n === 0 ? '—' : String(n);
      },
    },
    {
      key: 'detail',
      header: 'Field changes (old → new)',
      // The diff IS the safety mechanism for a manual-editor-width blast radius,
      // so the actual VALUES render inline — never behind a click. Long rows cap
      // at INLINE_CHANGES with an expander, so a 300-row file stays scannable
      // without hiding what the common case is about to do.
      render: (d) => {
        const isOpen = expanded.has(d.rowNo);
        const shown = isOpen ? d.changes : d.changes.slice(0, INLINE_CHANGES);
        const hidden = d.changes.length - shown.length;
        if (!d.changes.length && !d.findingsChanged) return <span className="small">—</span>;
        return (
          <>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {shown.map((c) => (
                <li key={c.column} className="small">
                  {c.review && <strong>[review] </strong>}
                  {COLUMN_LABELS[c.column]}: <em>{c.from}</em> → <strong>{c.to}</strong>
                </li>
              ))}
              {d.findingsChanged && (
                <li className="small">
                  <strong>[review] </strong>Findings: {d.findings.length} line(s) from the file —
                  analyst and CES-captured findings are preserved
                </li>
              )}
            </ul>
            {(hidden > 0 || isOpen) && (
              <Button variant="ghost" onClick={() => toggleRow(d.rowNo)}>
                {isOpen ? 'Show fewer' : `+${hidden} more`}
              </Button>
            )}
          </>
        );
      },
    },
    {
      key: 'outcome',
      header: 'Outcome',
      sortable: true,
      accessor: (d) => d.outcome,
      render: (d) => <span className="badge">{OUTCOME_LABELS[d.outcome]}</span>,
    },
    {
      key: 'warnings',
      header: 'Notes',
      render: (d) =>
        d.warnings.length ? <span className="small">{d.warnings.join(' ')}</span> : '',
    },
  ];

  const resultColumns: Column<ResultRow>[] = [
    { key: 'rowNo', header: '#', align: 'right', render: (r) => String(r.rowNo) },
    { key: 'reference', header: 'Reference', sortable: true, render: (r) => r.reference },
    {
      key: 'outcome',
      header: 'Outcome',
      sortable: true,
      render: (r) => <span className="badge">{r.outcome}</span>,
    },
    { key: 'fields', header: 'Fields', align: 'right', render: (r) => String(r.fields) },
    { key: 'message', header: 'Detail', render: (r) => r.message },
  ];

  return (
    <>
      <ImportBulkTabs />
      <Card>
        <div className="section-head">
          <div>
            <h3>Bulk update transactions</h3>
            <div className="small">
              Upload an Excel or CSV file to update transactions that are{' '}
              <strong>already in the register</strong>. Only the columns your file carries are
              written — a blank cell in a column you supplied is left alone, never cleared. Every
              change is shown before anything is saved. To ADD transactions, use Import instead.
            </div>
          </div>
          <div className="actions">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
              disabled={running}
              aria-label="Choose an Excel or CSV file of transaction updates"
              data-testid="bulk-update-file"
            />
            {(drafts || results || parseError) && (
              <Button variant="ghost" onClick={reset} disabled={running}>
                Clear
              </Button>
            )}
          </div>
        </div>
        {fileName && <div className="small">Selected: {fileName}</div>}
        {parseError && <ErrorState message="The file could not be used." detail={parseError} />}
        {parsed && (
          <div className="small" style={{ marginTop: 8 }}>
            <div>
              <strong>Columns this file will update:</strong>{' '}
              {parsed.presentColumns
                .filter((c) => c !== 'valueDisplay')
                .map((c) => COLUMN_LABELS[c])
                .join(', ')}
            </div>
            {parsed.insertOnlyColumns.length > 0 && (
              <div>
                <strong>Read but not updatable:</strong> {parsed.insertOnlyColumns.join(', ')} —
                these are set when a transaction is created and cannot be changed afterwards.
              </div>
            )}
            {parsed.ignoredHeaders.length > 0 && (
              <div>
                <strong>Ignored columns:</strong> {parsed.ignoredHeaders.join(', ')}
              </div>
            )}
          </div>
        )}
      </Card>

      {drafts && !results && (
        <Card>
          <div className="section-head">
            <div>
              <h3>Preview</h3>
              <div className="small">
                {writable.length.toLocaleString('en-ZA')} of {drafts.length.toLocaleString('en-ZA')}{' '}
                row(s) would change something ({changedFields.toLocaleString('en-ZA')} field
                change(s)). Nothing has been written yet.
              </div>
              {touchesReview && (
                <div className="small" style={{ marginTop: 4 }}>
                  <strong>This file changes review state</strong> — compliance levels, review
                  status, gaps status or findings, marked <strong>[review]</strong> below. Findings
                  merge: lines captured by a reviewer or a CES working paper are preserved; only
                  earlier file-sourced lines are replaced.
                </div>
              )}
            </div>
            <div className="actions">
              <Button
                variant="primary"
                onClick={() => void runUpdate()}
                disabled={running || writable.length === 0}
                data-testid="bulk-update-confirm"
              >
                {running
                  ? progress || 'Updating…'
                  : `Update ${writable.length.toLocaleString('en-ZA')} row(s)`}
              </Button>
            </div>
          </div>
          {drafts.length === 0 ? (
            <EmptyState message="No rows to preview." />
          ) : (
            <DataTable
              columns={previewColumns}
              rows={drafts}
              getRowKey={(d) => String(d.rowNo)}
              emptyMessage="No rows to preview."
            />
          )}
        </Card>
      )}

      {results && (
        <Card>
          <div className="section-head">
            <div>
              <h3>Update results</h3>
              <div className="small">
                {summarise(
                  fileName,
                  results.filter((r) => r.outcome === 'Updated').length,
                  results.filter((r) => r.outcome === 'Error').length,
                  results.reduce((n, r) => n + r.fields, 0),
                )}
              </div>
            </div>
            <div className="actions">
              <Button variant="ghost" onClick={reset}>
                Upload another file
              </Button>
            </div>
          </div>
          <DataTable
            columns={resultColumns}
            rows={results}
            getRowKey={(r) => String(r.rowNo)}
            emptyMessage="No results."
          />
        </Card>
      )}
    </>
  );
}
